import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type { ContextDefinition, ContextLevel, ContextSegment, LevelSettings, SortOrder } from 'types/context';
import type { UnifiedFilters } from 'types/filters';
import ContextLevelSummaryAtom from '../../atoms/ContextLevelSummary/ContextLevelSummaryAtom';
import { renderFilterExpression } from '../../../utils/renderFilterExpression';
import { readFilterStringValues } from '../../../utils/readFilterStringValues';
import './ContextSummaryMolecule.scss';

export interface ResolvedTypeInfo {
  displayName: string;
  avatar?: string;
}

export interface ContextSummaryContext {
  systemName: string;
  name: string;
  description?: string;
  definition: ContextDefinition;
  applicability?: UnifiedFilters | null;
  useCaseTags?: string[];
}

export interface ContextSummaryMoleculeProps {
  context: ContextSummaryContext;
  resolvedTypes?: Map<string, ResolvedTypeInfo>;
  defaultExpanded?: boolean;
  mode?: 'compact' | 'expanded' | 'toggleable' | 'inline';
  className?: string;
  'data-testid'?: string;
}

import { getLocalizedText } from '../../../utils/getLocalizedText';

const KEY = 'marketplace.approachExplorer.contextSummary';

function getDirectionSymbol(direction: 'inwards' | 'outwards' | 'both'): string {
  if (direction === 'both') return '↔';
  return direction === 'outwards' ? '→' : '←';
}

function getEnrichmentLabels(settings: LevelSettings, t: (key: string) => string): string[] {
  const labels: string[] = [];
  if (settings.includeStatus) labels.push(t(`${KEY}.enrichment.status`));
  if (settings.includeFlow) labels.push(t(`${KEY}.enrichment.flow`));
  if (settings.includeEdgeCount) labels.push(t(`${KEY}.enrichment.edgeCount`));
  if (settings.includeApproachDeployments) labels.push(t(`${KEY}.enrichment.approachDeployments`));
  if (settings.includePoints) labels.push(t(`${KEY}.enrichment.points`));
  return labels;
}

const CompactView: React.FC<{
  levels: ContextLevel[];
  resolvedTypes?: Map<string, ResolvedTypeInfo>;
  t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ levels, resolvedTypes, t }) => {
  const maxVisibleLevels = 3;
  const visibleLevels = levels.slice(0, maxVisibleLevels);
  const hiddenCount = levels.length - maxVisibleLevels;
  const primaryDirection = levels[0]?.direction || 'outwards';

  return (
    <div className="context-summary__compact">
      <div className="context-summary__levels-row">
        {visibleLevels.map((level, i) => (
          <React.Fragment key={level.id}>
            {i > 0 && (
              <span className="context-summary__direction-arrow" data-testid="direction-arrow">
                {getDirectionSymbol(level.direction)}
              </span>
            )}
            <ContextLevelSummaryAtom
              level={level}
              resolvedTypes={resolvedTypes}
              compact
              data-testid={`level-box-${i}`}
            />
          </React.Fragment>
        ))}
        {hiddenCount > 0 && (
          <span className="context-summary__more-indicator" data-testid="more-indicator">
            {t(`${KEY}.moreColumns`, { count: hiddenCount })}
          </span>
        )}
      </div>
      <div className="context-summary__summary-line">
        {t(`${KEY}.columns`, { count: levels.length })}
        {' · '}
        {t(`${KEY}.${primaryDirection}`)}
      </div>
    </div>
  );
};

function getInlinePillLabel(level: ContextLevel, resolvedTypes?: Map<string, ResolvedTypeInfo>): string {
  for (const f of level.filters?.propertyFilters || []) {
    if (f.property !== 'type') continue;
    const val = readFilterStringValues(f)[0];
    if (val) {
      const display = resolvedTypes?.get(val)?.displayName;
      if (display) return display;
      return val.replace(/^center_/, '').replaceAll('_', ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
    }
  }
  return getLocalizedText(level.label) || 'Level';
}

const InlineView: React.FC<{
  levels: ContextLevel[];
  resolvedTypes?: Map<string, ResolvedTypeInfo>;
}> = ({ levels, resolvedTypes }) => (
  <div className="context-summary__inline">
    <span className="context-summary__inline-start">
      <span className="context-summary__inline-dot--primary">●</span>
      <span className="context-summary__inline-dot--secondary">·</span>
    </span>
    {levels.map((level) => (
      <span key={level.id} className="context-summary__inline-step">
        <span className="context-summary__inline-free-arrow">
          {getDirectionSymbol(level.direction)}
        </span>
        <span className="context-summary__inline-pill">
          <span className="context-summary__inline-pill-arrow">
            {getDirectionSymbol(level.direction)}
          </span>
          <span className="context-summary__inline-pill-label">
            {getInlinePillLabel(level, resolvedTypes)}
          </span>
          {(level.repeat ?? 1) > 1 && (
            <span className="context-summary__inline-repeat">×{level.repeat}</span>
          )}
        </span>
      </span>
    ))}
  </div>
);

const ExpandedLevelPanel: React.FC<{
  level: ContextLevel;
  resolvedTypes?: Map<string, ResolvedTypeInfo>;
  t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ level, resolvedTypes, t }) => {
  const label = getLocalizedText(level.label);
  const allFilters = [
    ...(level.filters?.propertyFilters || []),
  ];

  return (
    <div className="context-summary__level-panel" data-testid={`level-panel-${level.id}`}>
      <div className="context-summary__level-header">
        <span className="context-summary__level-label">{label || t(`${KEY}.untitledLevel`)}</span>
        <span className="context-summary__level-direction">
          {getDirectionSymbol(level.direction)} {t(`${KEY}.${level.direction}`)}
        </span>
        {(level.repeat ?? 1) > 1 && (
          <span className="context-summary__level-repeat">
            {t(`${KEY}.repeat`, { count: level.repeat })}
          </span>
        )}
      </div>

      {allFilters.length > 0 && (
        <div className="context-summary__level-section">
          <span className="context-summary__section-label">{t(`${KEY}.filters`, { count: allFilters.length })}</span>
          <ul className="context-summary__filter-list">
            {allFilters.map((filter, i) => (
              <li key={i} className="context-summary__filter-item">
                {renderFilterExpression(filter, resolvedTypes)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {level.segments && level.segments.length > 0 && (
        <div className="context-summary__level-section">
          <span className="context-summary__section-label">{t(`${KEY}.segments`)}</span>
          {level.segments.map((seg: ContextSegment) => (
            <div key={seg.id} className="context-summary__segment">
              <span className="context-summary__segment-label">
                {getLocalizedText(seg.label) || seg.id}
              </span>
              {seg.filters?.propertyFilters?.map((f, i) => (
                <span key={i} className="context-summary__segment-filter">
                  {renderFilterExpression(f, resolvedTypes)}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      {level.sortOrder && level.sortOrder.length > 0 && (
        <div className="context-summary__level-section">
          <span className="context-summary__section-label">{t(`${KEY}.sortOrder`)}</span>
          <span className="context-summary__sort-value">
            {level.sortOrder.map((s: SortOrder) => `${s.property} ${s.orderBy}`).join(', ')}
          </span>
        </div>
      )}

      {level.settings && (
        <>
          {getEnrichmentLabels(level.settings, t).length > 0 && (
            <div className="context-summary__level-section">
              <span className="context-summary__section-label">{t(`${KEY}.settings`)}</span>
              <div className="context-summary__enrichment-list">
                {getEnrichmentLabels(level.settings, t).map((label, i) => (
                  <span key={i} className="context-summary__enrichment-badge">{label}</span>
                ))}
              </div>
            </div>
          )}
          {(level.settings.limit != null || level.settings.lazyLoad != null) && (
            <div className="context-summary__level-section">
              <span className="context-summary__section-label">{t(`${KEY}.pagination`)}</span>
              <span className="context-summary__pagination-value">
                {level.settings.limit != null && t(`${KEY}.limit`, { count: level.settings.limit })}
                {level.settings.limit != null && level.settings.lazyLoad != null && ' · '}
                {level.settings.lazyLoad != null && (
                  level.settings.lazyLoad ? t(`${KEY}.lazyLoadEnabled`) : t(`${KEY}.lazyLoadDisabled`)
                )}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ContextSummaryMolecule: React.FC<ContextSummaryMoleculeProps> = ({
  context,
  resolvedTypes,
  defaultExpanded = false,
  mode = 'toggleable',
  className,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const initialExpanded = (() => {
    if (mode === 'expanded') return true;
    if (mode === 'compact' || mode === 'inline') return false;
    return defaultExpanded;
  })();
  const [expanded, setExpanded] = useState(initialExpanded);
  const classes = ['context-summary', className].filter(Boolean).join(' ');

  if (!context.definition) {
    return (
      <div className={classes} data-testid={testId}>
        <p className="context-summary__not-available">{t(`${KEY}.definitionNotAvailable`)}</p>
      </div>
    );
  }

  const { levels } = context.definition;

  if (!levels || levels.length === 0) {
    return (
      <div className={classes} data-testid={testId}>
        <p className="context-summary__no-levels">{t(`${KEY}.noLevels`)}</p>
      </div>
    );
  }

  if (mode === 'inline') {
    return (
      <div className={classes} data-testid={testId}>
        <InlineView levels={levels} resolvedTypes={resolvedTypes} />
      </div>
    );
  }

  return (
    <div className={classes} data-testid={testId}>
      {expanded ? (
        <div className="context-summary__expanded">
          {levels.map((level) => (
            <ExpandedLevelPanel
              key={level.id}
              level={level}
              resolvedTypes={resolvedTypes}
              t={t}
            />
          ))}
          {context.definition.settings && (
            <div className="context-summary__global-settings" data-testid="global-settings">
              {context.definition.settings.maxDepth != null && (
                <span className="context-summary__setting">
                  {t(`${KEY}.maxDepth`, { count: context.definition.settings.maxDepth })}
                </span>
              )}
              {context.definition.settings.permissionCheck && (
                <span className="context-summary__setting">
                  {t(`${KEY}.permissionCheck`, { level: context.definition.settings.permissionCheck })}
                </span>
              )}
              {context.definition.settings.cacheable != null && (
                <span className="context-summary__setting">
                  {context.definition.settings.cacheable
                    ? t(`${KEY}.cacheEnabled`)
                    : t(`${KEY}.cacheDisabled`)}
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <CompactView levels={levels} resolvedTypes={resolvedTypes} t={t} />
      )}

      {mode === 'toggleable' && (
        <button
          type="button"
          className="context-summary__toggle"
          onClick={() => setExpanded((prev) => !prev)}
          data-testid="toggle-button"
        >
          <i className={`pi ${expanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
          {expanded ? t(`${KEY}.collapse`) : t(`${KEY}.expand`)}
        </button>
      )}
    </div>
  );
};

ContextSummaryMolecule.displayName = 'ContextSummaryMolecule';

export default ContextSummaryMolecule;
